/**
 * My Profile Page - Employee's personal information management
 */

import { useState, useRef } from 'react'
import { MaskedField } from '../components/MaskedField'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { employeeInfoApi } from '../api/employeeInfoApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import type {
  ProfileInfoInput,
  DocumentInput,
  EmergencyContactInput,
  FamilyMemberInput,
  AddressInput,
  BankAccountInput,
  DocumentType,
  DocumentCategory,
} from '../types/employee-info.types'
import {
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_CATEGORY_LABELS,
  RELATIONSHIP_LABELS,
  FAMILY_RELATIONSHIP_LABELS,
  ADDRESS_TYPE_LABELS,
  MARITAL_STATUS_LABELS,
  EDUCATION_LEVEL_LABELS,
  GENDER_LABELS,
  ACCOUNT_TYPE_LABELS,
} from '../types/employee-info.types'

// Indonesian month names
const INDONESIAN_MONTHS = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
]

// Format date to Indonesian convention (DD MMMM YYYY)
const formatDateID = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr
    const day = date.getDate()
    const month = INDONESIAN_MONTHS[date.getMonth()]
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  } catch {
    return dateStr
  }
}

// Validation helpers
const validateKTP = (value: string): string | null => {
  if (!value) return null // Optional field
  const cleaned = value.replace(/[\s-]/g, '')
  if (cleaned.length !== 16) return 'KTP number must be 16 digits'
  if (!/^\d+$/.test(cleaned)) return 'KTP number must contain only digits'
  return null
}

const validateNPWP = (value: string): string | null => {
  if (!value) return null // Optional field
  const cleaned = value.replace(/[\s.\-]/g, '')
  if (cleaned.length !== 16) return 'NPWP number must be 16 digits'
  if (!/^\d+$/.test(cleaned)) return 'NPWP number must contain only digits'
  return null
}

// Clean form data: convert empty strings to null for optional fields
const cleanFormData = <T extends Record<string, any>>(data: T): T => {
  const cleaned: Record<string, any> = {}
  for (const [key, value] of Object.entries(data)) {
    if (value === '') {
      cleaned[key] = null
    } else {
      cleaned[key] = value
    }
  }
  return cleaned as T
}

// Format field name for display (e.g., "npwp_number" -> "NPWP Number")
const formatFieldName = (field: string): string => {
  const specialCases: Record<string, string> = {
    'ktp_number': 'KTP Number',
    'npwp_number': 'NPWP Number',
    'bpjs_health_number': 'BPJS Health Number',
    'bpjs_employment_number': 'BPJS Employment Number',
    'date_of_birth': 'Date of Birth',
    'place_of_birth': 'Place of Birth',
    'passport_number': 'Passport Number',
    'passport_expiry': 'Passport Expiry',
    'graduation_year': 'Graduation Year',
    'highest_education': 'Highest Education',
    'education_institution': 'Education Institution',
    'education_major': 'Education Major',
    'marital_status': 'Marital Status',
    'blood_type': 'Blood Type',
    'contact_name': 'Contact Name',
    'phone_primary': 'Primary Phone',
    'phone_secondary': 'Secondary Phone',
    'relationship_type': 'Relationship',
    'full_name': 'Full Name',
    'is_primary': 'Primary',
    'is_dependent': 'Dependent',
    'is_beneficiary': 'Beneficiary',
    'street_address': 'Street Address',
    'postal_code': 'Postal Code',
    'address_type': 'Address Type',
    'effective_from': 'Effective From',
    'effective_to': 'Effective To',
    'bank_name': 'Bank Name',
    'bank_code': 'Bank Code',
    'branch_name': 'Branch Name',
    'swift_code': 'SWIFT Code',
    'account_number': 'Account Number',
    'account_holder_name': 'Account Holder Name',
    'account_type': 'Account Type',
    'document_type': 'Document Type',
    'document_category': 'Document Category',
    'document_number': 'Document Number',
    'issue_date': 'Issue Date',
    'expiry_date': 'Expiry Date',
    'issuing_authority': 'Issuing Authority',
  }

  if (specialCases[field]) return specialCases[field]

  // Default: convert snake_case to Title Case
  return field.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

// Extract error message from API response
const getErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data
  if (data?.error) {
    if (data.error.details && typeof data.error.details === 'object') {
      const details = Object.entries(data.error.details)
        .map(([field, msg]) => {
          const fieldName = formatFieldName(field)
          const message = typeof msg === 'string' ? msg : String(msg)
          // Remove "Value error, " prefix from Pydantic messages
          const cleanMsg = message.replace(/^Value error,\s*/i, '')
          return `${fieldName}: ${cleanMsg}`
        })
        .join('\n')
      return details || data.error.message || fallback
    }
    return data.error.message || fallback
  }
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      // Handle FastAPI validation error array format
      return data.detail.map((err: any) => {
        const field = err.loc?.slice(1).join('.') || 'Field'
        const fieldName = formatFieldName(field)
        const msg = err.msg || 'Invalid value'
        return `${fieldName}: ${msg}`
      }).join('\n')
    }
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail)
  }
  return error?.message || fallback
}

const MyProfilePage = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Active section for editing
  const [activeSection, setActiveSection] = useState<string | null>(null)

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    ktp_number?: string
    npwp_number?: string
  }>({})

  // Form states
  const [profileForm, setProfileForm] = useState<ProfileInfoInput>({})
  const [documentForm, setDocumentForm] = useState<DocumentInput>({
    document_type: 'other',
    document_category: 'other',
    title: '',
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [contactForm, setContactForm] = useState<EmergencyContactInput>({
    contact_name: '',
    relationship_type: 'other',
    phone_primary: '',
  })
  const [familyForm, setFamilyForm] = useState<FamilyMemberInput>({
    full_name: '',
    relationship_type: 'spouse',
  })
  const [addressForm, setAddressForm] = useState<AddressInput>({
    address_type: 'current',
    street_address: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'Indonesia',
  })
  const [bankForm, setBankForm] = useState<BankAccountInput>({
    bank_name: '',
    account_number: '',
    account_holder_name: '',
    account_type: 'savings',
  })

  // Queries
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['myProfile'],
    queryFn: employeeInfoApi.getMyProfile,
  })

  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['myDocuments'],
    queryFn: () => employeeInfoApi.getMyDocuments({ limit: 100 }),
  })

  const { data: contactsData, isLoading: contactsLoading } = useQuery({
    queryKey: ['myEmergencyContacts'],
    queryFn: () => employeeInfoApi.getMyEmergencyContacts({ limit: 100 }),
  })

  const { data: familyData, isLoading: familyLoading } = useQuery({
    queryKey: ['myFamilyMembers'],
    queryFn: () => employeeInfoApi.getMyFamilyMembers({ limit: 100 }),
  })

  const { data: addressesData, isLoading: addressesLoading } = useQuery({
    queryKey: ['myAddresses'],
    queryFn: () => employeeInfoApi.getMyAddresses({ limit: 100 }),
  })

  const { data: bankAccountsData, isLoading: bankAccountsLoading } = useQuery({
    queryKey: ['myBankAccounts'],
    queryFn: () => employeeInfoApi.getMyBankAccounts({ limit: 100 }),
  })

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: employeeInfoApi.updateMyProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myProfile'] })
      toast.success('Profile updated successfully')
      setActiveSection(null)
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update profile'))
      console.error('Profile update error:', error?.response?.data || error)
    },
  })

  const uploadDocumentMutation = useMutation({
    mutationFn: ({ file, metadata }: { file: File; metadata: DocumentInput }) =>
      employeeInfoApi.uploadDocument(file, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myDocuments'] })
      toast.success('Document uploaded successfully')
      setActiveSection(null)
      setSelectedFile(null)
      setDocumentForm({ document_type: 'other', document_category: 'other', title: '' })
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to upload document')),
  })

  const deleteDocumentMutation = useMutation({
    mutationFn: employeeInfoApi.deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myDocuments'] })
      toast.success('Document deleted successfully')
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to delete document')),
  })

  const createContactMutation = useMutation({
    mutationFn: employeeInfoApi.createEmergencyContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myEmergencyContacts'] })
      toast.success('Emergency contact added successfully')
      setActiveSection(null)
      setContactForm({ contact_name: '', relationship_type: 'other', phone_primary: '' })
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to add emergency contact')),
  })

  const deleteContactMutation = useMutation({
    mutationFn: employeeInfoApi.deleteEmergencyContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myEmergencyContacts'] })
      toast.success('Emergency contact deleted successfully')
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to delete emergency contact')),
  })

  const createFamilyMutation = useMutation({
    mutationFn: employeeInfoApi.createFamilyMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myFamilyMembers'] })
      toast.success('Family member added successfully')
      setActiveSection(null)
      setFamilyForm({ full_name: '', relationship_type: 'spouse' })
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to add family member')),
  })

  const deleteFamilyMutation = useMutation({
    mutationFn: employeeInfoApi.deleteFamilyMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myFamilyMembers'] })
      toast.success('Family member deleted successfully')
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to delete family member')),
  })

  const createAddressMutation = useMutation({
    mutationFn: employeeInfoApi.createAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myAddresses'] })
      toast.success('Address added successfully')
      setActiveSection(null)
      setAddressForm({
        address_type: 'current',
        street_address: '',
        city: '',
        province: '',
        postal_code: '',
        country: 'Indonesia',
      })
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to add address')),
  })

  const deleteAddressMutation = useMutation({
    mutationFn: employeeInfoApi.deleteAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myAddresses'] })
      toast.success('Address deleted successfully')
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to delete address')),
  })

  const createBankMutation = useMutation({
    mutationFn: employeeInfoApi.createBankAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myBankAccounts'] })
      toast.success('Bank account added successfully')
      setActiveSection(null)
      setBankForm({
        bank_name: '',
        account_number: '',
        account_holder_name: '',
        account_type: 'savings',
      })
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to add bank account')),
  })

  const deleteBankMutation = useMutation({
    mutationFn: employeeInfoApi.deleteBankAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myBankAccounts'] })
      toast.success('Bank account deleted successfully')
    },
    onError: (error: any) => toast.error(getErrorMessage(error, 'Failed to delete bank account')),
  })

  // Handlers
  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate KTP and NPWP
    const errors: { ktp_number?: string; npwp_number?: string } = {}
    const ktpError = validateKTP(profileForm.ktp_number || '')
    const npwpError = validateNPWP(profileForm.npwp_number || '')

    if (ktpError) errors.ktp_number = ktpError
    if (npwpError) errors.npwp_number = npwpError

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      toast.error('Please fix the validation errors')
      return
    }

    setValidationErrors({})
    updateProfileMutation.mutate(cleanFormData(profileForm))
  }

  const handleDocumentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) {
      toast.error('Please select a file')
      return
    }
    uploadDocumentMutation.mutate({ file: selectedFile, metadata: cleanFormData(documentForm) })
  }

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createContactMutation.mutate(cleanFormData(contactForm))
  }

  const handleFamilySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createFamilyMutation.mutate(cleanFormData(familyForm))
  }

  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createAddressMutation.mutate(cleanFormData(addressForm))
  }

  const handleBankSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createBankMutation.mutate(cleanFormData(bankForm))
  }

  const openProfileEdit = () => {
    if (profile) {
      setProfileForm({
        nickname: profile.nickname || '',
        gender: profile.gender,
        date_of_birth: profile.date_of_birth || '',
        place_of_birth: profile.place_of_birth || '',
        nationality: profile.nationality || '',
        religion: profile.religion || '',
        marital_status: profile.marital_status,
        blood_type: profile.blood_type || '',
        ktp_number: profile.ktp_number || '',
        npwp_number: profile.npwp_number || '',
        bpjs_health_number: profile.bpjs_health_number || '',
        bpjs_employment_number: profile.bpjs_employment_number || '',
        passport_number: profile.passport_number || '',
        passport_expiry: profile.passport_expiry || '',
        highest_education: profile.highest_education,
        education_institution: profile.education_institution || '',
        education_major: profile.education_major || '',
        graduation_year: profile.graduation_year,
      })
    }
    setValidationErrors({})
    setActiveSection('profile')
  }

  const isLoading = profileLoading || documentsLoading || contactsLoading || familyLoading || addressesLoading || bankAccountsLoading

  if (isLoading) return <LoadingPage />

  const documents = documentsData?.data || []
  const contacts = contactsData?.data || []
  const family = familyData?.data || []
  const addresses = addressesData?.data || []
  const bankAccounts = bankAccountsData?.data || []

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 px-6 py-8 shadow-lg">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold text-white">My Profile</h1>
          <p className="text-indigo-200 mt-1">Manage your personal information and documents</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Info Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
                <p className="text-sm text-gray-500">Basic profile details</p>
              </div>
            </div>
            <button
              onClick={openProfileEdit}
              className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              Edit
            </button>
          </div>
          <div className="p-6">
            {activeSection === 'profile' ? (
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
                    <input
                      type="text"
                      value={profileForm.nickname || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, nickname: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      value={profileForm.gender || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, gender: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select gender</option>
                      {Object.entries(GENDER_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={profileForm.date_of_birth || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, date_of_birth: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Place of Birth</label>
                    <input
                      type="text"
                      value={profileForm.place_of_birth || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, place_of_birth: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                    <input
                      type="text"
                      value={profileForm.nationality || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, nationality: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                    <input
                      type="text"
                      value={profileForm.religion || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, religion: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
                    <select
                      value={profileForm.marital_status || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, marital_status: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select status</option>
                      {Object.entries(MARITAL_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Blood Type</label>
                    <input
                      type="text"
                      value={profileForm.blood_type || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, blood_type: e.target.value })}
                      placeholder="e.g., A+, B-, O+"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-gray-900 pt-4">ID Numbers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">KTP Number</label>
                    <input
                      type="text"
                      value={profileForm.ktp_number || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        setProfileForm({ ...profileForm, ktp_number: value })
                        // Clear error when user starts typing
                        if (validationErrors.ktp_number) {
                          setValidationErrors({ ...validationErrors, ktp_number: undefined })
                        }
                      }}
                      onBlur={(e) => {
                        const error = validateKTP(e.target.value)
                        if (error) {
                          setValidationErrors({ ...validationErrors, ktp_number: error })
                        }
                      }}
                      maxLength={20}
                      placeholder="16 digits"
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        validationErrors.ktp_number ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    />
                    {validationErrors.ktp_number && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.ktp_number}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NPWP Number</label>
                    <input
                      type="text"
                      value={profileForm.npwp_number || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        setProfileForm({ ...profileForm, npwp_number: value })
                        // Clear error when user starts typing
                        if (validationErrors.npwp_number) {
                          setValidationErrors({ ...validationErrors, npwp_number: undefined })
                        }
                      }}
                      onBlur={(e) => {
                        const error = validateNPWP(e.target.value)
                        if (error) {
                          setValidationErrors({ ...validationErrors, npwp_number: error })
                        }
                      }}
                      maxLength={16}
                      placeholder="16 digits"
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        validationErrors.npwp_number ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    />
                    {validationErrors.npwp_number && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.npwp_number}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">BPJS Health Number</label>
                    <input
                      type="text"
                      value={profileForm.bpjs_health_number || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, bpjs_health_number: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">BPJS Employment Number</label>
                    <input
                      type="text"
                      value={profileForm.bpjs_employment_number || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, bpjs_employment_number: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passport Number</label>
                    <input
                      type="text"
                      value={profileForm.passport_number || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, passport_number: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passport Expiry</label>
                    <input
                      type="date"
                      value={profileForm.passport_expiry || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, passport_expiry: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-gray-900 pt-4">Education</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Highest Education</label>
                    <select
                      value={profileForm.highest_education || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, highest_education: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select education level</option>
                      {Object.entries(EDUCATION_LEVEL_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                    <input
                      type="text"
                      value={profileForm.education_institution || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, education_institution: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Major</label>
                    <input
                      type="text"
                      value={profileForm.education_major || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, education_major: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
                    <input
                      type="number"
                      value={profileForm.graduation_year || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, graduation_year: e.target.value ? parseInt(e.target.value) : null })}
                      min={1950}
                      max={new Date().getFullYear()}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSection(null)
                      setValidationErrors({})
                    }}
                    className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isPending}
                    className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="text-gray-900 font-medium">{user?.full_name || '-'}</span></div>
                <div><span className="text-gray-500">Email:</span> <span className="text-gray-900">{user?.email || '-'}</span></div>
                <div><span className="text-gray-500">Nickname:</span> <span className="text-gray-900">{profile?.nickname || '-'}</span></div>
                <div><span className="text-gray-500">Gender:</span> <span className="text-gray-900">{profile?.gender ? GENDER_LABELS[profile.gender] : '-'}</span></div>
                <div><span className="text-gray-500">Date of Birth:</span> <span className="text-gray-900">{formatDateID(profile?.date_of_birth)}</span></div>
                <div><span className="text-gray-500">Place of Birth:</span> <span className="text-gray-900">{profile?.place_of_birth || '-'}</span></div>
                <div><span className="text-gray-500">Nationality:</span> <span className="text-gray-900">{profile?.nationality || '-'}</span></div>
                <div><span className="text-gray-500">Religion:</span> <span className="text-gray-900">{profile?.religion || '-'}</span></div>
                <div><span className="text-gray-500">Marital Status:</span> <span className="text-gray-900">{profile?.marital_status ? MARITAL_STATUS_LABELS[profile.marital_status] : '-'}</span></div>
                <div><span className="text-gray-500">Blood Type:</span> <span className="text-gray-900">{profile?.blood_type || '-'}</span></div>
                <MaskedField label="KTP (NIK)" value={profile?.ktp_number} variant="inline" />
                <MaskedField label="NPWP" value={profile?.npwp_number} variant="inline" />
                <MaskedField label="BPJS Health" value={profile?.bpjs_health_number} variant="inline" />
                <MaskedField label="BPJS Employment" value={profile?.bpjs_employment_number} variant="inline" />
                <div><span className="text-gray-500">Passport Number:</span> <span className="text-gray-900">{profile?.passport_number || '-'}</span></div>
                <div><span className="text-gray-500">Passport Expiry:</span> <span className="text-gray-900">{formatDateID(profile?.passport_expiry)}</span></div>
                <div><span className="text-gray-500">Education:</span> <span className="text-gray-900">{profile?.highest_education ? EDUCATION_LEVEL_LABELS[profile.highest_education] : '-'}</span></div>
                <div><span className="text-gray-500">Institution:</span> <span className="text-gray-900">{profile?.education_institution || '-'}</span></div>
                <div><span className="text-gray-500">Major:</span> <span className="text-gray-900">{profile?.education_major || '-'}</span></div>
                <div><span className="text-gray-500">Graduation Year:</span> <span className="text-gray-900">{profile?.graduation_year || '-'}</span></div>
              </div>
            )}
          </div>
        </div>

        {/* Documents Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
                <p className="text-sm text-gray-500">{documents.length} document(s) uploaded</p>
              </div>
            </div>
            <button
              onClick={() => setActiveSection('document')}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upload Document
            </button>
          </div>
          <div className="p-6">
            {activeSection === 'document' && (
              <form onSubmit={handleDocumentSubmit} className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                    <select
                      value={documentForm.document_type}
                      onChange={(e) => setDocumentForm({ ...documentForm, document_type: e.target.value as DocumentType })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      value={documentForm.document_category}
                      onChange={(e) => setDocumentForm({ ...documentForm, document_category: e.target.value as DocumentCategory })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={documentForm.title}
                      onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Number</label>
                    <input
                      type="text"
                      value={documentForm.document_number || ''}
                      onChange={(e) => setDocumentForm({ ...documentForm, document_number: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Authority</label>
                    <input
                      type="text"
                      value={documentForm.issuing_authority || ''}
                      onChange={(e) => setDocumentForm({ ...documentForm, issuing_authority: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                    <input
                      type="date"
                      value={documentForm.issue_date || ''}
                      onChange={(e) => setDocumentForm({ ...documentForm, issue_date: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                    <input
                      type="date"
                      value={documentForm.expiry_date || ''}
                      onChange={(e) => setDocumentForm({ ...documentForm, expiry_date: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, PNG, DOC, DOCX</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setActiveSection(null); setSelectedFile(null); }}
                    className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadDocumentMutation.isPending}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploadDocumentMutation.isPending ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </form>
            )}

            {documents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No documents uploaded yet</p>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{doc.title}</p>
                        <p className="text-sm text-gray-500">{DOCUMENT_TYPE_LABELS[doc.document_type]} • {doc.original_filename}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {doc.is_expired && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Expired</span>
                      )}
                      <button
                        onClick={() => deleteDocumentMutation.mutate(doc.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Emergency Contacts Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Emergency Contacts</h2>
                <p className="text-sm text-gray-500">{contacts.length} contact(s)</p>
              </div>
            </div>
            <button
              onClick={() => setActiveSection('contact')}
              className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Add Contact
            </button>
          </div>
          <div className="p-6">
            {activeSection === 'contact' && (
              <form onSubmit={handleContactSubmit} className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
                    <input
                      type="text"
                      value={contactForm.contact_name}
                      onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship *</label>
                    <select
                      value={contactForm.relationship_type}
                      onChange={(e) => setContactForm({ ...contactForm, relationship_type: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    >
                      {Object.entries(RELATIONSHIP_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Primary) *</label>
                    <input
                      type="tel"
                      value={contactForm.phone_primary}
                      onChange={(e) => setContactForm({ ...contactForm, phone_primary: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Secondary)</label>
                    <input
                      type="tel"
                      value={contactForm.phone_secondary || ''}
                      onChange={(e) => setContactForm({ ...contactForm, phone_secondary: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={contactForm.email || ''}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isPrimary"
                      checked={contactForm.is_primary || false}
                      onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                      className="w-4 h-4 text-red-600 rounded"
                    />
                    <label htmlFor="isPrimary" className="text-sm text-gray-700">Set as primary contact</label>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setActiveSection(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg">Cancel</button>
                  <button type="submit" disabled={createContactMutation.isPending} className="px-6 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50">
                    {createContactMutation.isPending ? 'Adding...' : 'Add Contact'}
                  </button>
                </div>
              </form>
            )}

            {contacts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No emergency contacts added yet</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{contact.contact_name}</p>
                          <p className="text-xs text-gray-500">{RELATIONSHIP_LABELS[contact.relationship_type]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {contact.is_primary && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Primary</span>
                        )}
                        <button
                          onClick={() => deleteContactMutation.mutate(contact.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-sm">
                      <p className="text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{contact.phone_primary}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Family Members Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Family Members</h2>
                <p className="text-sm text-gray-500">{family.length} member(s)</p>
              </div>
            </div>
            <button
              onClick={() => setActiveSection('family')}
              className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Add Member
            </button>
          </div>
          <div className="p-6">
            {activeSection === 'family' && (
              <form onSubmit={handleFamilySubmit} className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={familyForm.full_name}
                      onChange={(e) => setFamilyForm({ ...familyForm, full_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship *</label>
                    <select
                      value={familyForm.relationship_type}
                      onChange={(e) => setFamilyForm({ ...familyForm, relationship_type: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    >
                      {Object.entries(FAMILY_RELATIONSHIP_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      value={familyForm.gender || ''}
                      onChange={(e) => setFamilyForm({ ...familyForm, gender: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    >
                      <option value="">Select gender</option>
                      {Object.entries(GENDER_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={familyForm.date_of_birth || ''}
                      onChange={(e) => setFamilyForm({ ...familyForm, date_of_birth: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
                    <input
                      type="text"
                      value={familyForm.occupation || ''}
                      onChange={(e) => setFamilyForm({ ...familyForm, occupation: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={familyForm.is_dependent || false}
                        onChange={(e) => setFamilyForm({ ...familyForm, is_dependent: e.target.checked })}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Tax Dependent</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={familyForm.is_beneficiary || false}
                        onChange={(e) => setFamilyForm({ ...familyForm, is_beneficiary: e.target.checked })}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Insurance Beneficiary</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setActiveSection(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg">Cancel</button>
                  <button type="submit" disabled={createFamilyMutation.isPending} className="px-6 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">
                    {createFamilyMutation.isPending ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              </form>
            )}

            {family.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No family members added yet</p>
            ) : (
              <div className="space-y-4">
                {family.map((member) => (
                  <div key={member.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{member.full_name}</p>
                          <p className="text-xs text-gray-500">{FAMILY_RELATIONSHIP_LABELS[member.relationship_type]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.is_dependent && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Dependent</span>
                        )}
                        {member.is_beneficiary && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Beneficiary</span>
                        )}
                        <button
                          onClick={() => deleteFamilyMutation.mutate(member.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Addresses Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Addresses</h2>
                <p className="text-sm text-gray-500">{addresses.length} address(es)</p>
              </div>
            </div>
            <button
              onClick={() => setActiveSection('address')}
              className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Add Address
            </button>
          </div>
          <div className="p-6">
            {activeSection === 'address' && (
              <form onSubmit={handleAddressSubmit} className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Type *</label>
                    <select
                      value={addressForm.address_type}
                      onChange={(e) => setAddressForm({ ...addressForm, address_type: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    >
                      {Object.entries(ADDRESS_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="addressPrimary"
                      checked={addressForm.is_primary || false}
                      onChange={(e) => setAddressForm({ ...addressForm, is_primary: e.target.checked })}
                      className="w-4 h-4 text-orange-600 rounded"
                    />
                    <label htmlFor="addressPrimary" className="text-sm text-gray-700">Set as primary address</label>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                    <textarea
                      value={addressForm.street_address}
                      onChange={(e) => setAddressForm({ ...addressForm, street_address: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      rows={2}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">RT/RW</label>
                    <input
                      type="text"
                      value={addressForm.rt_rw || ''}
                      onChange={(e) => setAddressForm({ ...addressForm, rt_rw: e.target.value })}
                      placeholder="e.g., 001/002"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kelurahan</label>
                    <input
                      type="text"
                      value={addressForm.kelurahan || ''}
                      onChange={(e) => setAddressForm({ ...addressForm, kelurahan: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kecamatan</label>
                    <input
                      type="text"
                      value={addressForm.kecamatan || ''}
                      onChange={(e) => setAddressForm({ ...addressForm, kecamatan: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      value={addressForm.city}
                      onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Province *</label>
                    <input
                      type="text"
                      value={addressForm.province}
                      onChange={(e) => setAddressForm({ ...addressForm, province: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                    <input
                      type="text"
                      value={addressForm.postal_code}
                      onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={addressForm.country}
                      onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setActiveSection(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg">Cancel</button>
                  <button type="submit" disabled={createAddressMutation.isPending} className="px-6 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50">
                    {createAddressMutation.isPending ? 'Adding...' : 'Add Address'}
                  </button>
                </div>
              </form>
            )}

            {addresses.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No addresses added yet</p>
            ) : (
              <div className="space-y-4">
                {addresses.map((address) => (
                  <div key={address.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{ADDRESS_TYPE_LABELS[address.address_type]}</p>
                          <p className="text-xs text-gray-500">{address.city}, {address.province}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {address.is_primary && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Primary</span>
                        )}
                        <button
                          onClick={() => deleteAddressMutation.mutate(address.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bank Accounts Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Bank Accounts</h2>
                <p className="text-sm text-gray-500">{bankAccounts.length} account(s)</p>
              </div>
            </div>
            <button
              onClick={() => setActiveSection('bank')}
              className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Add Account
            </button>
          </div>
          <div className="p-6">
            {activeSection === 'bank' && (
              <form onSubmit={handleBankSubmit} className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                    <input
                      type="text"
                      value={bankForm.bank_name}
                      onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Code</label>
                    <input
                      type="text"
                      value={bankForm.bank_code || ''}
                      onChange={(e) => setBankForm({ ...bankForm, bank_code: e.target.value })}
                      placeholder="e.g., 014 for BCA"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                    <input
                      type="text"
                      value={bankForm.account_number}
                      onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name *</label>
                    <input
                      type="text"
                      value={bankForm.account_holder_name}
                      onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                    <select
                      value={bankForm.account_type}
                      onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    >
                      {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
                    <input
                      type="text"
                      value={bankForm.branch_name || ''}
                      onChange={(e) => setBankForm({ ...bankForm, branch_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="bankPrimary"
                      checked={bankForm.is_primary || false}
                      onChange={(e) => setBankForm({ ...bankForm, is_primary: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <label htmlFor="bankPrimary" className="text-sm text-gray-700">Set as primary salary account</label>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setActiveSection(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg">Cancel</button>
                  <button type="submit" disabled={createBankMutation.isPending} className="px-6 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50">
                    {createBankMutation.isPending ? 'Adding...' : 'Add Account'}
                  </button>
                </div>
              </form>
            )}

            {bankAccounts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No bank accounts added yet</p>
            ) : (
              <div className="space-y-4">
                {bankAccounts.map((account) => (
                  <div key={account.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{account.bank_name}</p>
                          {account.bank_code && (
                            <p className="text-xs text-gray-500">Code: {account.bank_code}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {account.is_primary && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Primary</span>
                        )}
                        <button
                          onClick={() => deleteBankMutation.mutate(account.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <p className="text-gray-500">Account Holder</p>
                        <p className="font-medium text-gray-900">{account.account_holder_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Account Type</p>
                        <p className="font-medium text-gray-900">{ACCOUNT_TYPE_LABELS[account.account_type]}</p>
                      </div>
                      <div className="col-span-2">
                        <MaskedField label="Account Number" value={account.account_number} variant="inline" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyProfilePage
